/**
 * Display names for the `Feature` enum in `@wend/shared`.
 *
 * The enum values are the contract (`reach` · `connect` · `flow` · `sense`);
 * these are the product names they belong to. Enabling a flag is provisioning,
 * not billing.
 *
 * SLICE 65 SPLIT REACH IN TWO. Before it, "reach" was every way a workspace
 * talked to people — email and WhatsApp campaigns and Meta ads — and its copy
 * here read "Campaigns across every channel". Now `reach` is paid acquisition
 * (Meta ads on the workspace's own ad account) and `connect` is the owned
 * channels (email and WhatsApp). Migration 0048 gave every workspace that had
 * Reach a Connect row too, so the split took nothing away from anybody; the
 * copy below is what a staff member reads when deciding which half to grant.
 * The "Agentic"/"Agent" prefixes went with it: they named a promise, not
 * software, and the tenant app never used them.
 *
 * FLOW IS BUILT (slices 68–69). Until then this paragraph argued for keeping
 * an unbuilt Flow selectable — hiding its checkbox would have dropped the row
 * from any workspace provisioned with it on the next unrelated save, because a
 * PATCH replaces the whole set — and its line said "not built yet" so nobody
 * sold it. That is superseded: the engine and the builder ship, so its line
 * now says what a staff member is granting — triggers, waits and branches —
 * and that it sends through the workspace's own Connect accounts, so a Flow
 * grant without Connect is a grant of a builder that cannot send.
 *
 * ORDER. `FEATURES` is `PILLAR_ORDER` — Reach, Connect, Sense, Flow — the order
 * the tenant app's sidebar and Overview show them in, so staff and tenants see
 * the same list the same way. It used to be the enum's declared order, on the
 * argument that a stable order keeps the audit string stable. That job belongs
 * to `renderFeatureSet` in `@wend/shared`, which de-duplicates and sorts before
 * anything is recorded, so changing the display order here changes no audit
 * row; the forms still rebuild their arrays from `FEATURES`, so the order is at
 * least stable within this app.
 */
import { PILLAR_ORDER, type Feature } from '@wend/shared';

export interface FeatureCopy {
  readonly label: string;
  readonly description: string;
}

export const FEATURE_COPY: Readonly<Record<Feature, FeatureCopy>> = {
  reach: {
    label: 'Reach',
    description: "Paid ads on the workspace's own Meta ad account.",
  },
  connect: {
    label: 'Connect',
    description: "Email and WhatsApp campaigns from the workspace's own accounts.",
  },
  sense: {
    label: 'Sense',
    description: 'Funnel, pressure, outcomes and the Today queue.',
  },
  flow: {
    label: 'Flow',
    description: 'Journeys: triggers, waits and branches; built. Sends through Connect.',
  },
};

/** Stable order for checkboxes and chips: the display order, `PILLAR_ORDER`. */
export const FEATURES: readonly Feature[] = PILLAR_ORDER;

/**
 * A tenant's enabled set, re-ordered for display.
 *
 * The API returns features in the database enum's order (reach, connect, flow,
 * sense — `ORDER BY feature`), which is an accident of when each label was
 * added. Chips read in `PILLAR_ORDER` instead, the same order as the checkboxes
 * on the edit form above them.
 */
export function inPillarOrder(features: readonly Feature[]): readonly Feature[] {
  return FEATURES.filter((feature) => features.includes(feature));
}

/** Short form for a chip in a dense table row. */
export function shortFeatureLabel(feature: Feature): string {
  return feature.charAt(0).toUpperCase() + feature.slice(1);
}
